<script lang="ts" setup>
import { computed } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  BonusFormData,
  BonusFormEmits,
  BonusFormProps,
  IBonusCreateManuallyPayload,
} from '@modules/players-bonuses/submodules/components/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { useConfig } from '@modules/config/composables';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { ADMIN_PLAYERS_BONUSES_LIST_ROUTE_NAME } from '@modules/players-bonuses/constants';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { utcDateToLocal } from '@utils/date';
import { isDate } from '@utils/guards';
import { formatDate } from '@vueuse/core';

const props = defineProps<BonusFormProps>();
const emits = defineEmits<BonusFormEmits>();

const { getTranslation } = useI18n();
const { config } = useConfig();

const productListOptions = computed<IFormSelectInputOption<string>[]>(() => {
  if (!config.value?.products_list) {
    return [];
  }

  return config.value.products_list.map((item) => ({
    value: item,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, `PRODUCTS.RS.${item}`).value,
  }));
});

const formSchema = computed<FormSchema<BonusFormData>>(() => ({
  aasm_state: {
    type: EFormInputTypes.SELECT,
    label: props.aasmStateLabel,
    value: props.data ? props.data.bonus.aasm_state : productListOptions.value[0].value,
    options: productListOptions.value,
    required: true,
  },
  cid: {
    type: EFormInputTypes.TEXT,
    label: props.cidLabel,
    value: props.data ? props.data.bonus.cid : '',
    required: true,
  },
  bonus_amount: {
    type: EFormInputTypes.NUMBER,
    label: props.bonusAmountLabel,
    value: props.data ? props.data.bonus.bonus_amount : 0,
    required: true,
  },
  date: {
    type: EFormInputTypes.DATE,
    label: props.dateLabel,
    value: props.data ? utcDateToLocal(props.data.bonus.date) : undefined,
    required: true,
  },
}));

function emitSubmit(values: BonusFormData): void {
  const payload: IBonusCreateManuallyPayload = { ...values, date: isDate(values.date) ? formatDate(values.date, RESPONSE_DATE_FORMAT) : values.date };
  emits('submit', payload);
}

function emitInteracted(event: string): void {
  emits('interacted', event);
}
</script>

<template>
  <VForm v-data-qa-auto-id="'BonusChangeForm'"
    :schema="formSchema"
    :submit-btn-label="props.btnSubmitLabel"
    :custom-errors="props.errors"
    :is-loading="props.isLoading"
    @submit="emitSubmit($event)"
    @interacted="emitInteracted($event)"
  >
    <template #secondaryBtn>
      <VBackButton
        :label="props.backButtonLabel"
        :to="{ name: ADMIN_PLAYERS_BONUSES_LIST_ROUTE_NAME }"
      />
    </template>
  </VForm>
</template>
